/* eslint-disable prettier/prettier */
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import appSettings from "appsettings.json";

import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Grid from "@mui/material/Grid";
import Alert from "@mui/material/Alert";
import Snackbar from "@mui/material/Snackbar";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";

import { useMaterialUIController, setLayout, addCartItem } from "context";
import CommonFooter from "components/CommonFooter";
import CommonStoreHeader from "components/CommonStoreHeader";
import CategoryMenuController from "components/CategoryMenuController";
import AppLoader from "components/AppLoader";
import {
  fetchAllPublicCategories,
  fetchPublicCategories,
  fetchPublicCategoryNames,
  fetchPublicListingsByCategory,
} from "utils/categoryApi";

import decor1 from "assets/images/home-decor-1.jpg";
import decor2 from "assets/images/home-decor-2.jpg";
import decor3 from "assets/images/home-decor-3.jpg";
import decor4 from "assets/images/home-decor-4.jpeg";
import team1 from "assets/images/team-1.jpg";
import team2 from "assets/images/team-2.jpg";
import team3 from "assets/images/team-3.jpg";
import team4 from "assets/images/team-4.jpg";
import productPlaceholder from "assets/images/product-placeholder.svg";

const toSlug = (value) =>
  value
    .toLowerCase()
    .replace(/&/g, "and")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");

const allProducts = [
  {
    category: "Tea & Coffee",
    name: "Khao Shong Coffee",
    size: "50.00 g",
    price: "Rs. 984.00",
    mrp: "Rs. 1230.00",
    discount: "20.00% OFF",
    image: decor1,
  },
  {
    category: "Tea & Coffee",
    name: "Mocha Coffee Mix",
    size: "220.00 g",
    price: "Rs. 1,172.00",
    mrp: "Rs. 1445.00",
    discount: "20.00% OFF",
    image: decor2,
  },
  {
    category: "Tea & Coffee",
    name: "Bogawantalawa Tea",
    size: "1.00 kg",
    price: "Rs. 2,240.00",
    mrp: "Rs. 3200.00",
    discount: "30.00% OFF",
    image: decor3,
  },
  {
    category: "Tea & Coffee",
    name: "Watawala Tea",
    size: "400.00 g",
    price: "Rs. 1,140.00",
    mrp: "Rs. 1380.00",
    discount: "15.00% OFF",
    image: decor4,
  },
  {
    category: "Household",
    name: "Liquid Floor Cleaner",
    size: "750 ml",
    price: "Rs. 880.00",
    mrp: "Rs. 1020.00",
    discount: "10.00% OFF",
    image: team1,
  },
  {
    category: "Household",
    name: "Laundry Care Pack",
    size: "1.20 kg",
    price: "Rs. 1,640.00",
    mrp: "Rs. 1980.00",
    discount: "18.00% OFF",
    image: team2,
  },
  {
    category: "Beverages",
    name: "Orange Juice Mix",
    size: "1.00 L",
    price: "Rs. 760.00",
    mrp: "Rs. 920.00",
    discount: "12.00% OFF",
    image: team3,
  },
  {
    category: "Beverages",
    name: "Energy Drink Combo",
    size: "6 x 250 ml",
    price: "Rs. 2,020.00",
    mrp: "Rs. 2420.00",
    discount: "16.00% OFF",
    image: team4,
  },
  {
    category: "Baby Products",
    name: "Baby Care Essentials",
    size: "5 pcs",
    price: "Rs. 2,950.00",
    mrp: "Rs. 3600.00",
    discount: "18.00% OFF",
    image: decor1,
  },
  {
    category: "Dairy",
    name: "Full Cream Milk Pack",
    size: "1.00 L",
    price: "Rs. 620.00",
    mrp: "Rs. 690.00",
    discount: "9.00% OFF",
    image: decor2,
  },
  {
    category: "Food Cupboard",
    name: "Pasta & Sauce Combo",
    size: "850 g",
    price: "Rs. 1,290.00",
    mrp: "Rs. 1520.00",
    discount: "15.00% OFF",
    image: decor3,
  },
  {
    category: "Food Cupboard",
    name: "Breakfast Cereal",
    size: "500 g",
    price: "Rs. 1,030.00",
    mrp: "Rs. 1210.00",
    discount: "14.00% OFF",
    image: decor4,
  },
];

const promoCollections = {
  "flat-40-off": {
    title: "Flat 40% OFF",
    subtitle: "Hair care essentials and shampoos",
    productNames: [
      "Liquid Floor Cleaner",
      "Laundry Care Pack",
      "Baby Care Essentials",
      "Full Cream Milk Pack",
    ],
  },
  "upto-30-off": {
    title: "Up to 30% OFF",
    subtitle: "Home cleaning and safety products",
    productNames: [
      "Liquid Floor Cleaner",
      "Laundry Care Pack",
      "Pasta & Sauce Combo",
      "Breakfast Cereal",
    ],
  },
  "chocolate-25-discount": {
    title: "25% Discount",
    subtitle: "Chocolates and festive treats",
    productNames: [
      "Breakfast Cereal",
      "Pasta & Sauce Combo",
      "Orange Juice Mix",
      "Energy Drink Combo",
    ],
  },
  "nutritious-1-cup": {
    title: "Nutritious 1 Cup",
    subtitle: "Breakfast drinks and instant mixes",
    productNames: ["Khao Shong Coffee", "Mocha Coffee Mix", "Bogawantalawa Tea", "Watawala Tea"],
  },
  "weekend-savings": {
    title: "Weekend Savings",
    subtitle: "Family packs and pantry stock-up",
    productNames: [
      "Baby Care Essentials",
      "Full Cream Milk Pack",
      "Pasta & Sauce Combo",
      "Breakfast Cereal",
    ],
  },
  "buy-2-get-1": {
    title: "Buy 2 Get 1",
    subtitle: "Fresh picks and selected essentials",
    productNames: [
      "Orange Juice Mix",
      "Energy Drink Combo",
      "Liquid Floor Cleaner",
      "Full Cream Milk Pack",
    ],
  },
};

function CategoryProducts() {
  const [, dispatch] = useMaterialUIController();
  const navigate = useNavigate();
  const { categorySlug } = useParams();
  const [searchParams] = useSearchParams();
  const [categories, setCategories] = useState([]);
  const [categoryDetails, setCategoryDetails] = useState([]);
  const [dbProducts, setDbProducts] = useState([]);
  const [isLoadingCategories, setIsLoadingCategories] = useState(true);
  const [isLoadingListings, setIsLoadingListings] = useState(false);
  const [isAddingToCart, setIsAddingToCart] = useState(false);
  const [cartNotice, setCartNotice] = useState({ open: false, message: "", severity: "success" });
  const promoId = searchParams.get("promo");

  const parsePrice = (value) => {
    if (typeof value === "number") {
      return Number.isFinite(value) ? value : 0;
    }

    if (typeof value !== "string") {
      return 0;
    }

    const normalized = value.replace(/,/g, "");
    const match = normalized.match(/-?\d+(\.\d+)?/);
    if (!match) {
      return 0;
    }

    const parsed = Number(match[0]);
    return Number.isFinite(parsed) ? parsed : 0;
  };

  const showCartNotice = (message, severity = "success") => {
    setCartNotice({ open: true, message, severity });
  };

  const handleAddToCart = async (product) => {
    const token = localStorage.getItem("token");
    if (!token) {
      navigate("/authentication/mobile-sign-in");
      return;
    }

    setIsAddingToCart(true);
    try {
      await saveMemberListing(product.id);
      addCartItem(dispatch, {
        id: Number(product.id),
        listingId: Number(product.id),
        name: product.name,
        size: product.size,
        image: product.image,
        price: parsePrice(product.price),
      });
      showCartNotice(`${product.name} added to cart.`);
    } catch (error) {
      console.error("Failed to save member listing:", error);
      showCartNotice("Failed to add item to cart. Please try again.", "warning");
    } finally {
      setIsAddingToCart(false);
    }
  };

  const saveMemberListing = async (listingId) => {
    const token = localStorage.getItem("token");
    if (!token) return;

    const numericListingId = Number(listingId);
    if (!numericListingId) return;

    const memberId = Number(localStorage.getItem("memberId") || 0);
    const response = await fetch(
      `${appSettings.baseUrl}${appSettings.endpoints.addMemberListing}`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          memberId,
          listingId: numericListingId,
          status: "Cart",
        }),
      }
    );

    if (!response.ok) {
      throw new Error("Failed to save cart item.");
    }
  };

  useEffect(() => {
    setLayout(dispatch, "page");
    return () => setLayout(dispatch, "dashboard");
  }, [dispatch]);

  useEffect(() => {
    let isMounted = true;

    const loadCategories = async () => {
      try {
        setIsLoadingCategories(true);
        const [categoryNames, allCategoriesPayload] = await Promise.all([
          fetchPublicCategoryNames(),
          fetchAllPublicCategories(),
        ]);
        if (isMounted) {
          setCategories(categoryNames);
          setCategoryDetails(allCategoriesPayload);
        }
      } catch (error) {
        console.error("Failed to load category menu:", error);
      } finally {
        if (isMounted) {
          setIsLoadingCategories(false);
        }
      }
    };

    loadCategories();

    return () => {
      isMounted = false;
    };
  }, []);

  const selectedCategory = useMemo(() => {
    const matchedFromAll = categoryDetails.find(
      (category) => toSlug(category.name) === categorySlug
    );
    if (matchedFromAll?.name) return matchedFromAll.name;

    const matchedFromMenu = categories.find((category) => toSlug(category) === categorySlug);
    return matchedFromMenu || categories[0] || "";
  }, [categoryDetails, categorySlug, categories]);

  const selectedPromo = useMemo(() => {
    if (!promoId) return null;
    return promoCollections[promoId] || null;
  }, [promoId]);

  useEffect(() => {
    let isMounted = true;

    const loadListings = async () => {
      if (!selectedCategory || selectedPromo) {
        setIsLoadingListings(false);
        setDbProducts([]);
        return;
      }

      try {
        setIsLoadingListings(true);
        const selectedCategoryDetail = categoryDetails.find(
          (category) => category.name === selectedCategory
        );
        const categoryId = Number(selectedCategoryDetail?.id || 0);

        if (!categoryId) {
          setIsLoadingListings(false);
          setDbProducts([]);
          return;
        }

        const listings = await fetchPublicListingsByCategory(categoryId);
        if (!isMounted) return;

        const mappedListings = listings.map((item) => {
          const images = Array.isArray(item?.imageUrls) ? item.imageUrls : [];
          const primaryImage = images.find(Boolean) || "";
          const imageUrl = primaryImage.startsWith("/")
            ? `${appSettings.baseUrl}${primaryImage}`
            : primaryImage;

          const priceValue = Number(item?.price || 0);

          return {
            id: item?.id || `${selectedCategory}-${item?.title || "item"}`,
            category: item?.categoryName || selectedCategory,
            name: item?.title || "Unnamed Item",
            description: item?.description || "",
            size: `${item?.quantity ?? 0} pcs`,
            price: `Rs. ${priceValue.toFixed(2)}`,
            mrp: `Rs. ${priceValue.toFixed(2)}`,
            discount: "0.00% OFF",
            image: imageUrl || productPlaceholder,
          };
        });

        setDbProducts(mappedListings);
      } catch (error) {
        console.error("Failed to load category listings:", error);
        if (isMounted) setDbProducts([]);
      } finally {
        if (isMounted) setIsLoadingListings(false);
      }
    };

    loadListings();

    return () => {
      isMounted = false;
    };
  }, [categoryDetails, selectedCategory, selectedPromo]);

  const categoryProducts = useMemo(() => {
    if (selectedPromo) {
      const matchedPromoProducts = allProducts.filter((product) =>
        selectedPromo.productNames.includes(product.name)
      );
      return matchedPromoProducts.length > 0 ? matchedPromoProducts : allProducts.slice(0, 8);
    }

    return dbProducts;
  }, [dbProducts, selectedPromo]);

  const isCategoryPageLoading = !selectedPromo && (isLoadingCategories || isLoadingListings);

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "#f5f5f5" }}>
      <CommonStoreHeader>
        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={1.2}
          alignItems={{ xs: "flex-start", md: "center" }}
        >
          <Box sx={{ width: "100%" }}>
            <CategoryMenuController
              categories={categories}
              activeCategory={selectedPromo ? undefined : selectedCategory}
            />
          </Box>
        </Stack>
      </CommonStoreHeader>

      <Box sx={{ px: { xs: 1.5, sm: 2.5, md: 3.5 }, py: 1.2 }}>
        {!isCategoryPageLoading && (
          <Typography sx={{ fontSize: { xs: "1.6rem", md: "2rem" }, fontWeight: 700 }}>
            Showing Products for{" "}
            <Box component="span" sx={{ color: "#2E7D32" }}>
              {selectedPromo ? selectedPromo.title : selectedCategory || "Category"}
            </Box>
          </Typography>
        )}
        {selectedPromo && (
          <Typography sx={{ color: "#475569", mt: 0.4, fontWeight: 500 }}>
            {selectedPromo.subtitle}
          </Typography>
        )}
      </Box>

      <Box sx={{ px: { xs: 1.5, sm: 2.5, md: 3.5 }, pb: 4 }}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            {isCategoryPageLoading ? (
              <Box
                sx={{
                  minHeight: 320,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <AppLoader size={42} />
              </Box>
            ) : !selectedPromo && categoryProducts.length === 0 ? (
              <Box
                sx={{
                  minHeight: 320,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Typography sx={{ color: "#475569", fontWeight: 600 }}>
                  No items found for this category.
                </Typography>
              </Box>
            ) : (
              <Grid container spacing={2}>
                {categoryProducts.map((product) => (
                  <Grid
                    item
                    xs={12}
                    sm={6}
                    xl={3}
                    key={`${selectedPromo ? promoId : selectedCategory}-${product.name}`}
                  >
                    <Card sx={{ borderRadius: 2, border: "1px solid #d1d5db", height: "100%" }}>
                      <CardContent>
                        <Stack>
                          <Box sx={{ display: "flex", justifyContent: "center", pt: 0.5 }}>
                            <Box
                              component="img"
                              src={product.image}
                              alt={product.name}
                              onError={(event) => {
                                event.currentTarget.onerror = null;
                                event.currentTarget.src = productPlaceholder;
                              }}
                              sx={{
                                width: 170,
                                height: 170,
                                objectFit: "contain",
                                borderRadius: 1,
                              }}
                            />
                          </Box>
                        </Stack>

                        <Typography sx={{ mt: 1.8, minHeight: 50, fontWeight: 700 }}>
                          {product.name}
                        </Typography>
                        <Typography
                          sx={{
                            color: "#475569",
                            fontSize: "0.88rem",
                            minHeight: 40,
                            mb: 0.4,
                            display: "-webkit-box",
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: "vertical",
                            overflow: "hidden",
                          }}
                        >
                          {product.description || " "}
                        </Typography>
                        <Typography sx={{ mt: 1.4, fontWeight: 800, fontSize: "1.1rem" }}>
                          {product.price}
                        </Typography>

                        <Button
                          fullWidth
                          variant="outlined"
                          disabled={isAddingToCart}
                          onClick={() => {
                            handleAddToCart(product);
                          }}
                          sx={{
                            borderColor: "#43A047",
                            color: "#2E7D32",
                            fontWeight: 700,
                            "&:hover": {
                              borderColor: "#2E7D32",
                              bgcolor: "#E8F5E9",
                              color: "#1B5E20",
                            },
                          }}
                        >
                          ADD
                        </Button>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            )}
          </Grid>
        </Grid>
      </Box>

      {isAddingToCart && (
        <Box
          sx={{
            position: "fixed",
            inset: 0,
            zIndex: 2000,
            bgcolor: "rgba(255,255,255,0.55)",
            backdropFilter: "blur(1px)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <AppLoader size={40} color="#b71c2a" />
        </Box>
      )}

      <Snackbar
        open={cartNotice.open}
        autoHideDuration={2200}
        onClose={() => setCartNotice((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={() => setCartNotice((prev) => ({ ...prev, open: false }))}
          severity={cartNotice.severity}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {cartNotice.message}
        </Alert>
      </Snackbar>

      <CommonFooter />
    </Box>
  );
}

export default CategoryProducts;
